import React, { useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { playerAPI } from '../services/api';
import { navigateFromPlayerNotification } from '../utils/playerNotificationNavigation';
import {
  clearPlayerNotificationBadgeCache,
  invalidatePlayerNotificationQueries,
} from '../utils/playerNotificationQueries';

const BRAND = { blue: '#043663', gray: '#E5E7EB', lightBg: '#f8fafc' };

const PageTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 1.25rem 0;
`;

const Toolbar = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
`;

const ToolbarBtn = styled.button`
  font-size: 0.8125rem;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid ${BRAND.gray};
  background: #fff;
  color: #0f172a;
  cursor: pointer;
  &:hover {
    background: ${BRAND.lightBg};
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const List = styled.div`
  background: #fff;
  border-radius: 12px;
  border: 1px solid ${BRAND.gray};
  overflow: hidden;
`;

const Item = styled.div`
  padding: 0;
  border-bottom: 1px solid ${BRAND.gray};
  background: ${(p) => (p.$unread ? 'rgba(4, 54, 99, 0.04)' : '#fff')};
  &:last-child {
    border-bottom: none;
  }
`;

const ItemRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;
`;

const ItemMain = styled.div`
  flex: 1;
  min-width: 0;
  padding: 1rem 0.35rem 1rem 1.25rem;
  cursor: ${(p) => (p.$clickable ? 'pointer' : 'default')};
  &:hover {
    background: ${(p) => (p.$clickable ? BRAND.lightBg : 'inherit')};
  }
`;

const DismissBtn = styled.button`
  flex-shrink: 0;
  margin: 0.65rem 0.5rem 0 0;
  border: none;
  background: transparent;
  color: #94a3b8;
  padding: 4px 8px;
  cursor: pointer;
  font-size: 1.15rem;
  line-height: 1;
  &:hover {
    color: #64748b;
  }
`;

const ItemTitle = styled.div`
  font-weight: 600;
  color: #0f172a;
  font-size: 0.9375rem;
  margin-bottom: 0.25rem;
`;

const ItemMessage = styled.div`
  font-size: 0.875rem;
  color: #64748b;
  line-height: 1.4;
`;

const ItemTime = styled.div`
  font-size: 0.75rem;
  color: #94a3b8;
  margin-top: 0.35rem;
`;

const Empty = styled.p`
  text-align: center;
  color: #64748b;
  padding: 2rem;
  margin: 0;
`;

export default function PlayerNotificationsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: teamsRes } = useQuery(['playerTeams'], () => playerAPI.getTeams(), {
    staleTime: 60 * 1000,
  });
  const rosterTeams = useMemo(
    () => (Array.isArray(teamsRes?.data) ? teamsRes.data : teamsRes?.data?.data) || [],
    [teamsRes]
  );

  const { data: res, isLoading } = useQuery(['playerNotifications'], () => playerAPI.getNotifications(), {
    staleTime: 20 * 1000,
    refetchOnWindowFocus: true,
  });
  const listRaw = Array.isArray(res?.data) ? res.data : res?.data?.data ?? [];
  const list = listRaw.filter(
    (n) => n?.type !== 'unread_message' && n?.type !== 'unread_message_reminder'
  );

  useEffect(() => {
    queryClient.invalidateQueries(['playerNotificationCount']);
  }, [queryClient]);

  const markRead = useMutation((id) => playerAPI.markNotificationRead(id), {
    onSuccess: () => invalidatePlayerNotificationQueries(queryClient),
  });

  const markAllRead = useMutation(() => playerAPI.markAllNotificationsRead(), {
    onSuccess: () => {
      clearPlayerNotificationBadgeCache(queryClient);
      invalidatePlayerNotificationQueries(queryClient);
    },
  });

  const clearAll = useMutation(() => playerAPI.clearAllNotifications(), {
    onSuccess: () => {
      clearPlayerNotificationBadgeCache(queryClient);
      invalidatePlayerNotificationQueries(queryClient);
    },
  });

  const deleteOne = useMutation((id) => playerAPI.deleteNotification(id), {
    onSuccess: () => invalidatePlayerNotificationQueries(queryClient),
  });

  const handleClick = async (n) => {
    if (!n.readAt) markRead.mutate(n.id);
    await navigateFromPlayerNotification(n, {
      navigate: (path) => navigate(path),
      rosterTeams,
      playerAPIGetTeams: () => playerAPI.getTeams(),
      queryClient,
    });
  };

  const getLinkLabel = useCallback(
    (type) => {
      if (type === 'match_found') return t('playerNotificationsPage.linkViewProposal');
      if (type === 'match_accepted' || type === 'score_submitted') return t('playerNotificationsPage.linkOpenFindMatch');
      if (type === 'unread_message' || type === 'unread_message_reminder') {
        return t('playerNotificationsPage.linkOpenMessages');
      }
      if (type === 'team_join_request') return t('playerNotificationsPage.linkJoinRequests');
      return t('playerNotificationsPage.linkView');
    },
    [t]
  );

  const isClickable = (n) => {
    if (n.matchId || n.proposalId || n.conversationId) return true;
    if (n.type === 'team_join_request') return true;
    if (n.leagueMatchId && n.teamId) return true;
    if (n.type === 'team_disabled' || n.type === 'removed_from_team') return true;
    return false;
  };

  if (isLoading) {
    return (
      <>
        <PageTitle>{t('playerNotificationsPage.title')}</PageTitle>
        <p style={{ color: '#64748b' }}>{t('playerNotificationsPage.loading')}</p>
      </>
    );
  }

  return (
    <>
      <PageTitle>{t('playerNotificationsPage.title')}</PageTitle>
      {list.length > 0 && (
        <Toolbar>
          <ToolbarBtn type="button" disabled={markAllRead.isLoading} onClick={() => markAllRead.mutate()}>
            {t('playerNotificationsPage.markAllRead')}
          </ToolbarBtn>
          <ToolbarBtn
            type="button"
            disabled={clearAll.isLoading}
            onClick={() => {
              if (!window.confirm(t('playerNotificationsPage.confirmClearAll'))) return;
              clearAll.mutate();
            }}
          >
            {t('playerNotificationsPage.clearAll')}
          </ToolbarBtn>
        </Toolbar>
      )}
      {list.length === 0 ? (
        <List>
          <Empty>{t('playerNotificationsPage.empty')}</Empty>
        </List>
      ) : (
        <List>
          {list.map((n) => (
            <Item key={n.id} $unread={!n.readAt}>
              <ItemRow>
                <ItemMain
                  $clickable={isClickable(n)}
                  role={isClickable(n) ? 'button' : undefined}
                  tabIndex={isClickable(n) ? 0 : undefined}
                  onClick={() => isClickable(n) && handleClick(n)}
                  onKeyDown={(e) => {
                    if (!isClickable(n)) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleClick(n);
                    }
                  }}
                >
                  <ItemTitle>{n.title}</ItemTitle>
                  <ItemMessage>{n.message}</ItemMessage>
                  <ItemTime>
                    {new Date(n.createdAt).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}
                    {isClickable(n) && (
                      <span style={{ marginLeft: '0.5rem', color: BRAND.blue, fontWeight: 600 }}>
                        → {getLinkLabel(n.type)}
                      </span>
                    )}
                  </ItemTime>
                </ItemMain>
                <DismissBtn
                  type="button"
                  title={t('playerNotificationsPage.dismissOne')}
                  aria-label={t('playerNotificationsPage.dismissOne')}
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteOne.mutate(n.id);
                  }}
                >
                  ×
                </DismissBtn>
              </ItemRow>
            </Item>
          ))}
        </List>
      )}
    </>
  );
}
